// Kit 01 · items 13 to 22.
//
//   13 Text input      14 Textarea        15 Select          16 Combobox
//   17 Search input    18 Multi-select    19 Number input    20 Password
//   21 Date picker     22 Date range picker
//
// redline: height 36 (search 32) · padding 0 12 · radius 8 · border 1
//          value 14/400 · chevron 14 trailing · menu radius 12 · row 34
//
// The shape shared by all ten: a BOX owns the geometry and the border, and the
// <input> inside it owns only the text. A UA stylesheet cannot then move the
// redline, and an affix (icon, kbd hint, stepper, reveal, chips) sits in the
// box's flex flow instead of being positioned over an input's padding.

import { useId, useState } from "react";
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";
import { Calendar, ChevronDown, Eye, EyeOff, Minus, Plus, Search, X } from "lucide-react";
import { Icon } from "./icon";
import { cx, stateAttr } from "./kit-state";
import type { KitState, Stateful } from "./kit-state";

/* ── the box ────────────────────────────────────────────────────────────── */

interface FieldBoxProps extends Stateful {
  children: ReactNode;
  className?: string;
  /** Drives the placeholder ink. Not a state: an empty field at rest and an
      empty field on hover are both empty. */
  filled?: boolean;
  onClick?: () => void;
}

function FieldBox({ children, className, state, filled, onClick }: FieldBoxProps) {
  return (
    <div
      className={cx("k-field", className)}
      data-filled={filled === undefined ? undefined : String(filled)}
      aria-invalid={state === "error" || undefined}
      onClick={onClick}
      {...stateAttr(state)}
    >
      {children}
    </div>
  );
}

/** A field's own disabled/readonly flags follow from its state, so a caller
    cannot produce a box that looks disabled and still takes a caret. */
function inputFlags(state?: KitState) {
  return {
    disabled: state === "disabled" || undefined,
    readOnly: state === "readonly" || undefined,
  };
}

/* ── 13 · text input ────────────────────────────────────────────────────── */

export interface TextInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size">, Stateful {
  className?: string;
}

export function TextInput({ state, className, value, defaultValue, ...rest }: TextInputProps) {
  const filled = Boolean(value ?? defaultValue) || state === "filled";
  return (
    <FieldBox state={state} className={className} filled={filled}>
      <input
        className="k-field__input"
        value={value}
        defaultValue={defaultValue}
        {...inputFlags(state)}
        {...rest}
      />
    </FieldBox>
  );
}

/* ── 14 · textarea ──────────────────────────────────────────────────────────
   redline: min-height 84 · padding 12 · radius 8 · resize vertical only
   Horizontal resize would let a user drag the field out of its column. */

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, Stateful {}

export function Textarea({ state, className, ...rest }: TextareaProps) {
  return (
    <textarea
      className={cx("k-textarea", className)}
      aria-invalid={state === "error" || undefined}
      disabled={state === "disabled" || rest.disabled}
      readOnly={state === "readonly" || rest.readOnly}
      {...stateAttr(state)}
      {...rest}
    />
  );
}

/* ── 17 · search ────────────────────────────────────────────────────────────
   redline: height 32 · icon 14 leading · clear 14 trailing · kbd hint 10 mono
   The clear affordance appears only when there is something to clear. */

export interface SearchInputProps extends TextInputProps {
  /** The keyboard hint shown at rest, e.g. "/". Hidden once the field fills,
      because the shortcut that focused it is not news while you are typing. */
  hint?: string;
  onClear?: () => void;
}

export function SearchInput({ state, className, hint, onClear, value, defaultValue, ...rest }: SearchInputProps) {
  const filled = Boolean(value ?? defaultValue) || state === "filled";
  return (
    <FieldBox state={state} className={cx("k-field--search", className)} filled={filled}>
      <span className="k-field__affix"><Icon icon={Search} size="sm" /></span>
      <input className="k-field__input" value={value} defaultValue={defaultValue} {...inputFlags(state)} {...rest} />
      {filled && onClear ? (
        <button type="button" className="k-field__affix" aria-label="Clear search" onClick={onClear}>
          <Icon icon={X} size="sm" />
        </button>
      ) : hint && !filled ? (
        <span className="k-kbd" aria-hidden="true">{hint}</span>
      ) : null}
    </FieldBox>
  );
}

/* ── 19 · number ────────────────────────────────────────────────────────────
   redline: height 36 · stepper 20 wide · value tabular, right aligned
   Right aligned and tabular so a column of these lines up on the units digit;
   a proportional font puts 1,111 and 8,888 at different widths. */

export interface NumberInputProps extends TextInputProps {
  onStep?: (direction: 1 | -1) => void;
}

export function NumberInput({ state, className, onStep, value, defaultValue, ...rest }: NumberInputProps) {
  const filled = value !== undefined && value !== "" ? true : Boolean(defaultValue) || state === "filled";
  const inert = state === "disabled" || state === "readonly";
  return (
    <FieldBox state={state} className={cx("k-field--number", className)} filled={filled}>
      <input
        className="k-field__input"
        inputMode="numeric"
        value={value}
        defaultValue={defaultValue}
        {...inputFlags(state)}
        {...rest}
      />
      <span className="k-stepper" aria-hidden="true">
        <button type="button" tabIndex={-1} disabled={inert} onClick={() => onStep?.(1)}>
          <Icon icon={Plus} size="sm" />
        </button>
        <button type="button" tabIndex={-1} disabled={inert} onClick={() => onStep?.(-1)}>
          <Icon icon={Minus} size="sm" />
        </button>
      </span>
    </FieldBox>
  );
}

/* ── 20 · password ──────────────────────────────────────────────────────────
   redline: height 36 · reveal 28 trailing · dots 8
   The reveal is a real toggle with a name that changes, so a screen reader
   hears what it will do rather than what it is. */

export function PasswordInput({ state, className, value, defaultValue, ...rest }: TextInputProps) {
  const [shown, setShown] = useState(false);
  const filled = Boolean(value ?? defaultValue) || state === "filled";
  return (
    <FieldBox state={state} className={cx("k-field--password", className)} filled={filled}>
      <input
        className="k-field__input"
        type={shown ? "text" : "password"}
        value={value}
        defaultValue={defaultValue}
        {...inputFlags(state)}
        {...rest}
      />
      <button
        type="button"
        className="k-icon-btn"
        aria-label={shown ? "Hide password" : "Show password"}
        aria-pressed={shown}
        disabled={state === "disabled"}
        onClick={() => setShown((s) => !s)}
      >
        <Icon icon={shown ? EyeOff : Eye} size="sm" />
      </button>
    </FieldBox>
  );
}

/* ── 15, 21, 22 · triggers that open something ──────────────────────────────
   One trigger shape, three payloads. The chevron is trailing and the box is
   space-between, so the value can grow to the chevron and stop. */

export interface TriggerProps extends Stateful {
  /** The current value, or the placeholder when there is none. */
  children: ReactNode;
  filled?: boolean;
  onClick?: () => void;
  className?: string;
  /** Which glyph sits at the end. Select uses a chevron, the date pickers a
      calendar, because a date field that opened a list would be lying. */
  affix?: "chevron" | "calendar";
  ariaLabel?: string;
  expanded?: boolean;
  /** So a FieldRow's label can point at it. Added in Phase 2: item 57's form
      modal is the first place a trigger sits inside a labelled row. */
  id?: string;
  /** The panel this opens, for a screen reader following the popup. */
  ariaControls?: string;
}

export function FieldTrigger({
  children, filled, onClick, state, className, affix = "chevron", ariaLabel, expanded,
  id, ariaControls,
}: TriggerProps) {
  return (
    <button
      type="button"
      id={id}
      aria-controls={ariaControls}
      className={cx("k-field", "k-field--trigger", className)}
      data-filled={String(Boolean(filled))}
      aria-invalid={state === "error" || undefined}
      aria-haspopup="listbox"
      aria-expanded={expanded}
      aria-label={ariaLabel}
      disabled={state === "disabled"}
      onClick={onClick}
      {...stateAttr(state)}
    >
      <span className="k-menu__label">{children}</span>
      <Icon icon={affix === "calendar" ? Calendar : ChevronDown} size="sm" />
    </button>
  );
}

/* ── 16 · combobox ──────────────────────────────────────────────────────────
   redline: height 36 · match highlight weight 600 · empty row 34
   The highlight is WEIGHT, not colour: colour here would compete with the
   accent that means "measured outcome" everywhere else in the product. */

export interface ComboOption {
  value: string;
  label: string;
}

export interface ComboboxProps extends Stateful {
  options: ComboOption[];
  query: string;
  onQueryChange?: (q: string) => void;
  onSelect?: (value: string) => void;
  placeholder?: string;
  /** The string shown when nothing matches. Kit 07's canonical set governs
      the wording; it is passed in rather than written here so one screen
      cannot paraphrase it. */
  emptyLabel: string;
  className?: string;
  label: string;
}

/** Split a label around the first case-insensitive occurrence of the query,
    so the highlight is derived from the two strings rather than from an index
    the caller has to keep in sync. */
export function splitMatch(label: string, query: string): [string, string, string] {
  if (!query) return [label, "", ""];
  const at = label.toLowerCase().indexOf(query.toLowerCase());
  if (at < 0) return [label, "", ""];
  return [label.slice(0, at), label.slice(at, at + query.length), label.slice(at + query.length)];
}

export function Combobox({
  options, query, onQueryChange, onSelect, placeholder, emptyLabel, state, className, label,
}: ComboboxProps) {
  const listId = useId();
  const open = state === "open" || state === "empty";
  const shown = state === "empty" ? [] : options;

  return (
    <div className={cx("k-vstack", className)} style={{ gap: "var(--sp-2)" }}>
      <FieldBox state={state === "empty" ? "open" : state} filled={Boolean(query)}>
        <input
          className="k-field__input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-label={label}
          autoComplete="off"
          placeholder={placeholder}
          value={query}
          onChange={(e) => onQueryChange?.(e.target.value)}
          {...inputFlags(state)}
        />
      </FieldBox>
      {open ? (
        <div className="k-menu" id={listId} role="listbox" aria-label={label}>
          {shown.length === 0 ? (
            <div className="k-menu__empty">{emptyLabel}</div>
          ) : (
            shown.map((option) => {
              const [before, hit, after] = splitMatch(option.label, query);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="k-menu__row"
                  onClick={() => onSelect?.(option.value)}
                >
                  <span className="k-menu__label">
                    {before}
                    {hit ? <span className="k-match">{hit}</span> : null}
                    {after}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

/* ── 18 · multi-select with chips ───────────────────────────────────────────
   redline: chip height 22 · chip radius 6 · remove glyph 14 · gap 8
   The box grows to its chips rather than scrolling them, because a chip that
   is half visible cannot be removed and cannot be read. */

export interface MultiSelectProps extends Stateful {
  selected: string[];
  onRemove?: (value: string) => void;
  placeholder: string;
  className?: string;
  label: string;
}

export function MultiSelect({ selected, onRemove, placeholder, state, className, label }: MultiSelectProps) {
  const filled = selected.length > 0 && state !== "empty";
  return (
    <div
      className={cx("k-field", "k-field--chips", className)}
      data-filled={String(filled)}
      aria-invalid={state === "error" || undefined}
      role="group"
      aria-label={label}
      {...stateAttr(state)}
    >
      {filled ? (
        selected.map((value) => (
          <span className="k-chip" key={value}>
            {value}
            <button
              type="button"
              className="k-chip__remove"
              aria-label={`Remove ${value}`}
              disabled={state === "disabled"}
              onClick={() => onRemove?.(value)}
            >
              <Icon icon={X} size="sm" />
            </button>
          </span>
        ))
      ) : (
        <span className="k-menu__label">{placeholder}</span>
      )}
    </div>
  );
}
